import { lazy, Suspense, useEffect, useRef, useState, useSyncExternalStore, type ReactNode, type RefObject } from 'react';
import type { Project } from '@aimo/contracts/web';
import type { ApiClient } from '../../api.js';
import { shouldSubmitComposer } from '../../composer-state.js';
import { askText } from '../prompt-dialog.js';
import { MODES, projectTitle, readFavorites, relativeTime, STARTERS, startIntent, visibleProjects, writeFavorites, type StartMode, type StitchUnifiedCallbacks } from '../../stitch-landing.js';
import { Button } from '../button.js';
import { LanguageSwitch } from '../language-switch.js';
import { AccountAvatar } from '../account-usage.js';
import { Icon } from '../icon.js';
import { cn } from '../utils.js';
import { graphite, ProjectLibrary } from './project-library.js';
import { LibrariesSection } from './libraries.js';
import { text } from './messages.js';
import { useProjectPreview } from './project-thumbnail.js';
import { StarterArt } from './starter-art.js';
import { pick } from '../../i18n.js';

const CanvasTutorial = lazy(() => import('./canvas-loop-tutorial.js'));
const tutorialText = pick({
  zh: { badge: '最小循环 · 一图看懂', opening: '正在打开画布教程…', action: '打开画布教程：' },
  en: { badge: 'Design loop · Overview', opening: 'Opening tutorial…', action: 'Open canvas tutorial: ' },
});

export interface LandingPageProps {
  projects: Project[];
  accountName: string | null;
  api: ApiClient | null;
  callbacks: StitchUnifiedCallbacks;
}

const focusRing = 'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
const COLLAPSED_KEY = 'aimo.landing.libraryCollapsed';
const NARROW = '(max-width: 899px)';

function useNarrow(): boolean {
  return useSyncExternalStore(change => {
    const query = window.matchMedia(NARROW); query.addEventListener('change', change);
    return () => query.removeEventListener('change', change);
  }, () => window.matchMedia(NARROW).matches, () => false);
}

function readCollapsed(): boolean {
  try { return localStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; }
}

/** The home page: a full-height project library beside the one-sentence creator, recent projects and illustrated starters. */
export function LandingPage({ projects, accountName, api, callbacks }: LandingPageProps) {
  const narrow = useNarrow();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [favorites, setFavorites] = useState(() => new Set(readFavorites()));
  const [busy, setBusy] = useState<string | null>(null);
  const [mode, setMode] = useState<StartMode>('design');
  const [prompt, setPrompt] = useState('');
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const composing = useRef(false), compositionEndedAt = useRef<number | null>(null);
  const drawerToggle = useRef<HTMLButtonElement>(null);

  useEffect(() => { if (!narrow) setDrawerOpen(false); }, [narrow]);
  useEffect(() => {
    if (!drawerOpen) return;
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape' && !document.querySelector('[role="dialog"], [role="menu"]')) { setDrawerOpen(false); drawerToggle.current?.focus(); } };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [drawerOpen]);

  /** Each start disables its own control until the shell has finished with it. */
  const run = (key: string, work: () => Promise<void>) => {
    if (busy) return;
    setBusy(key);
    work().catch(() => undefined).finally(() => setBusy(current => current === key ? null : current));
  };
  const createBlank = () => {
    void askText(text.newProject, text.projectName, text.untitled(projects.length + 1)).then(name => { if (name?.trim()) run('create', () => callbacks.onCreateProject(name.trim())); });
  };
  const start = () => {
    const text = prompt.trim(); if (!text) return;
    run('start', () => callbacks.onStartDesign(startIntent(text, mode), projectTitle(text)));
  };
  const toggleFavorite = (projectId: string) => {
    setFavorites(current => {
      const next = new Set(current);
      if (next.has(projectId)) next.delete(projectId); else next.add(projectId);
      writeFavorites(next);
      return next;
    });
  };
  const changeCollapsed = (next: boolean) => {
    setCollapsed(next);
    try { localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0'); } catch { /* the choice lasts for this page only */ }
  };
  const openProject = (projectId: string) => { setDrawerOpen(false); callbacks.onOpenProject(projectId); };
  const recent = visibleProjects(projects, 'recent', '', favorites).slice(0, narrow ? 2 : 4);

  const library = (
    <ProjectLibrary projects={projects} api={api} signedIn={accountName !== null} favorites={favorites} onToggleFavorite={toggleFavorite}
      onOpenProject={openProject} onCreateProject={createBlank} creating={busy === 'create'}
      collapsed={collapsed && !narrow} onCollapsedChange={changeCollapsed} drawer={narrow} onCloseDrawer={() => { setDrawerOpen(false); drawerToggle.current?.focus(); }} />
  );

  return (
    <div className="flex h-dvh w-full overflow-hidden bg-(--md-sys-color-surface) font-sans text-foreground">
      {!narrow && library}
      {narrow && drawerOpen && (
        <>
          <button type="button" aria-label={text.closeLibrary} tabIndex={-1} onClick={() => setDrawerOpen(false)}
            className="fixed inset-0 z-40 cursor-default border-0 bg-(--md-sys-color-scrim)/40" />
          {library}
        </>
      )}
      <main className="flex min-w-0 flex-1 flex-col overflow-y-auto">
        <header className="sticky top-0 z-10 flex min-h-16 shrink-0 flex-wrap items-center justify-between gap-3 bg-(--md-sys-color-surface)/90 px-5 py-3 sm:h-16 sm:flex-nowrap sm:py-0 backdrop-blur">
          <div className="flex items-center gap-2">
            {narrow && (
              <>
                <Button ref={drawerToggle} variant="ghost" size="icon" className="size-10" aria-label={text.openLibrary} aria-expanded={drawerOpen} onClick={() => setDrawerOpen(true)}>
                  <Icon name="menu" size={22} />
                </Button>
                <span className="font-heading text-[17px] font-medium"><span aria-hidden className="text-primary">✦</span> AiMo</span>
                {/* Creating stays one click away while the library is folded into its drawer. */}
                {!drawerOpen && <Button variant="default" size="default" className={cn('ml-2', graphite)} disabled={busy === 'create'} onClick={createBlank}>{text.newProjectButton}</Button>}
              </>
            )}
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <nav aria-label={pick({ zh: '下载与反馈', en: 'Downloads and feedback' })} className="flex items-center gap-1">
              <Button asChild variant="ghost" size="default">
                <a href="https://gitea.aimo3d.org/han/aimo-output-releases/releases" target="_blank" rel="noopener noreferrer">{pick({ zh: '下载', en: 'Download' })}</a>
              </Button>
              <Button asChild variant="ghost" size="default">
                <a href="https://gitea.aimo3d.org/han/aimo-output-releases/issues" target="_blank" rel="noopener noreferrer">{pick({ zh: '问题反馈', en: 'Issues' })}</a>
              </Button>
            </nav>
          {accountName !== null
            ? <div className="flex items-center gap-2">
                <LanguageSwitch />
                <AccountAvatar accountName={accountName} api={api} />
                <Button variant="ghost" size="default" onClick={callbacks.onLogout}>{text.logout}</Button>
              </div>
            : <div className="flex items-center gap-2">
                <LanguageSwitch />
                <Button variant="outline" size="default" onClick={callbacks.onLogin}>{text.login}</Button>
              </div>}
          </div>
        </header>

        <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-14 px-6 pb-20 pt-[max(8px,4vh)]">
          <section aria-labelledby="aimo-hero-heading" className="flex flex-col items-center gap-8 motion-safe:animate-[landing-rise_480ms_cubic-bezier(0.22,1,0.36,1)_both]">
            <h1 id="aimo-hero-heading" className="m-0 text-center font-heading text-[clamp(32px,4.6vw,52px)] font-medium leading-[1.1] tracking-[-0.02em] text-foreground">{text.heading}</h1>
            <form onSubmit={event => { event.preventDefault(); start(); }}
              className="flex w-full max-w-[840px] flex-col gap-1 rounded-[28px] border border-transparent bg-(--md-sys-color-surface-container) p-3 text-left transition-[background-color,border-color] duration-200 focus-within:border-(--md-sys-color-outline-variant) focus-within:bg-(--md-sys-color-surface-container-low)">
              <textarea ref={textarea} rows={3} aria-label={text.intent} aria-keyshortcuts="Enter" placeholder={MODES[mode].placeholder} value={prompt}
                onChange={event => setPrompt(event.target.value)}
                onCompositionStart={() => { composing.current = true; }}
                onCompositionEnd={() => { composing.current = false; compositionEndedAt.current = Date.now(); }}
                onKeyDown={event => {
                  if (!shouldSubmitComposer({ key: event.key, shiftKey: event.shiftKey, altKey: event.altKey, isComposing: event.nativeEvent.isComposing, defaultPrevented: event.defaultPrevented, keyCode: event.keyCode }, composing.current, compositionEndedAt.current, Date.now())) return;
                  event.preventDefault(); start();
                }}
                className="min-h-20 w-full resize-none border-0 bg-transparent px-2.5 py-2 font-sans text-base leading-relaxed text-foreground outline-none placeholder:text-muted-foreground" />
              <div className="flex items-center justify-between gap-3">
                <div role="group" aria-label={text.taskType} className="flex flex-wrap gap-1">
                  {(Object.keys(MODES) as StartMode[]).map(key => (
                    <button key={key} type="button" aria-pressed={mode === key} onClick={() => setMode(key)}
                      className={cn('h-8 cursor-pointer rounded-full border-0 px-3.5 font-sans text-[13px] transition-all active:scale-[0.98]', focusRing,
                        mode === key ? 'bg-card font-medium text-foreground shadow-sm' : 'bg-transparent text-muted-foreground hover:text-foreground')}>
                      {MODES[key].label}
                    </button>
                  ))}
                </div>
                <Button type="submit" variant="default" size="icon" aria-label={text.start} title={text.startHint} className={cn('size-10 shrink-0 rounded-full', graphite)} disabled={!prompt.trim() || busy === 'start'}>
                  <Icon name="arrow_upward" size={20} />
                </Button>
              </div>
            </form>
          </section>

          {accountName !== null && recent.length > 0 && (
            <LandingSection id="aimo-recent-heading" title={text.recent}>
              <div className={showcaseGrid}>
                {recent.map(project => <RecentCard key={project.projectId} project={project} api={api} onOpen={() => openProject(project.projectId)} />)}
              </div>
            </LandingSection>
          )}

          {/* Experience libraries beside the projects: captured and recalled automatically, browsed and switched only here. */}
          {accountName !== null && api && <LibrariesSection api={api} />}

          <LandingSection id="aimo-starters-heading" title={text.starters}>
            <div className={showcaseGrid}>
              <StarterCard kind="blank" title={text.blankTitle} description={text.blankDescription}
                disabled={busy === 'create'} onClick={createBlank} />
              {STARTERS.map((starter, index) => (
                <StarterCard key={starter.title} kind={starter.mode} title={starter.title} description={starter.description}
                  replay={index === 0}
                  disabled={busy === `starter:${starter.title}`}
                  onClick={() => index === 0 ? setTutorialOpen(true) : run(`starter:${starter.title}`, () => callbacks.onStartDesign(startIntent(starter.prompt, starter.mode), starter.title))} />
              ))}
            </div>
          </LandingSection>
        </div>
      </main>
      {tutorialOpen && <Suspense fallback={<div role="status" className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl bg-card px-5 py-3 shadow-lg">{tutorialText.opening}</div>}>
        <CanvasTutorial onClose={() => setTutorialOpen(false)} onStart={() => {
          setTutorialOpen(false);
          const starter = STARTERS[0]!;
          run(`starter:${starter.title}`, () => callbacks.onStartDesign(startIntent(starter.prompt, starter.mode), starter.title));
        }} />
      </Suspense>}
    </div>
  );
}

function LandingSection({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="flex flex-col gap-4">
      <h2 id={id} className="m-0 font-heading text-xl font-medium tracking-tight text-foreground">{title}</h2>
      {children}
    </section>
  );
}

const showcaseGrid = 'grid grid-cols-2 gap-4 md:grid-cols-4';

/**
 * A portrait showcase card: the picture floats sharp over a blurred, darkened copy of itself (or a quiet
 * neutral field), and the name sits on a scrim at the bottom.
 */
function Showcase({ backdrop, media, title, meta, onClick, disabled = false, description, tile }: {
  backdrop?: ReactNode; media: ReactNode; title: string; meta?: string; onClick(): void; disabled?: boolean; description?: string; tile?: RefObject<HTMLButtonElement | null>;
}) {
  return (
    <button ref={tile} type="button" disabled={disabled} onClick={onClick} title={description ?? title} aria-description={description}
      className={cn('group relative block aspect-[4/5] w-full cursor-pointer overflow-hidden rounded-2xl border-0 bg-linear-to-b from-(--md-sys-color-surface-container-high) to-(--md-sys-color-surface-container-highest) p-0 text-left font-sans shadow-sm',
        'transition-[transform,box-shadow] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-progress disabled:opacity-60 motion-reduce:transition-none motion-reduce:hover:translate-y-0', focusRing)}>
      {backdrop && <span aria-hidden className="absolute inset-0 scale-150 blur-2xl brightness-[.72] saturate-[.8]">{backdrop}</span>}
      <span aria-hidden className="absolute inset-x-[9%] bottom-[26%] top-[12%] flex items-center justify-center transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100">
        {media}
      </span>
      <span aria-hidden className="absolute inset-x-0 bottom-0 h-[42%] bg-linear-to-t from-(--md-sys-color-scrim)/70 via-(--md-sys-color-scrim)/20 to-transparent" />
      <span className="absolute inset-x-4 bottom-3.5 flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-sm font-medium text-(--md-sys-color-inverse-on-surface)">{title}</span>
        {meta && <span className="truncate text-xs text-(--md-sys-color-inverse-on-surface)/70">{meta}</span>}
      </span>
    </button>
  );
}

const floating = 'max-h-full max-w-full rounded-lg shadow-lg ring-1 ring-(--md-sys-color-scrim)/5';

/** An illustrated starter: the drawing carries the idea, the card only names it. */
function StarterCard({ kind, title, description, disabled, onClick, replay = false }: { kind: StartMode | 'blank'; title: string; description: string; disabled: boolean; onClick(): void; replay?: boolean }) {
  return <Showcase media={<span className="relative block w-full"><StarterArt kind={kind} className={cn('block aspect-[8/5] w-full bg-card', floating)} />{replay && <span className="absolute inset-0 flex items-center justify-center"><span className="flex size-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg"><span className="text-sm font-semibold">1–4</span></span></span>}</span>} title={title} meta={replay ? tutorialText.badge : undefined} description={replay ? tutorialText.action + title : description} disabled={disabled} onClick={onClick} />;
}

/** A recent project shown by its result image; without one, a frosted blank sheet. */
function RecentCard({ project, api, onOpen }: { project: Project; api: ApiClient | null; onOpen(): void }) {
  const { tile, url, fail } = useProjectPreview<HTMLButtonElement>(project, api);
  return (
    <Showcase tile={tile} title={project.name} meta={relativeTime(project.updatedAt) || undefined} onClick={onOpen}
      backdrop={url ? <img src={url} alt="" decoding="async" className="size-full object-cover" /> : undefined}
      media={url
        ? <img src={url} alt="" decoding="async" onError={fail} className={cn('block object-contain', floating)} />
        : <span className={cn('flex aspect-[8/5] w-full items-center justify-center bg-card/60 text-(--md-sys-color-outline) backdrop-blur-md', floating)}><Icon name="image" size={28} /></span>} />
  );
}
